import React, { useCallback, useState } from 'react';
import JsonLd from '../../../../components/layouts/JsonLd';
import MetaTags from '../../../../components/layouts/MetaTags';
import { MerchantSite } from '../../../../components/MerchantSite';
import MerchantProductDetailsPage from '../../../../templates/merchants/products/DetailTemplate/MerchantProductDetailsPage';
import PasswordPage from '../../../../molecules/merchant/products/MerchantProductProtectedPage';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import {
  allCategoriesCache,
  getCategory,
} from '../../../../infrastructure/api/backend/explore_api';
import { ProductRepositoryForSSR } from '../../../../infrastructure/repositories/ProductRepository';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { MerchantBookingPageApiResponse } from '../../../../interfaces/merchants/bookings';
import { MerchantProduct } from '../../../../interfaces/merchants/products';
import {
  fetchMerchantDetail,
  fetchProductDetail,
} from '../../../../infrastructure/api';
import {
  injectAppContextGetServerSideProps,
  AppGetServerSidePropsContext,
} from '../../../_app';
import { NextPage } from 'next';
import { RSToastContainer } from 'src/reservation-page/toast/RSToastContainer';
import { buildSiteUrl } from 'src/utils/request-url';

interface Context extends AppGetServerSidePropsContext {
  query: {
    productId: string;
    publicId: string;
  };
}

interface PageProps {
  pageProps?: {
    merchantDetail?: MerchantDetail;
    product: MerchantProduct;
    bookingServices: MerchantBookingPageApiResponse[];
    productStatus: string;
    category?: CategoryEntity;
    categories: CategoryEntity[];
  };
}

type Props = {
  session: string;
  token: string;
  requestId: string;
  isProtected?: boolean;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
  query: { publicId: string; productId: string };
} & PageProps;

type State = {
  isProtected?: boolean;
} & PageProps;

const fetchPageProps = async ({
  merchant,
  session,
  token,
  requestId,
  merchantPublicId,
  productPublicId,
}: {
  merchant?: MerchantDetail;
  session: string;
  token: string;
  requestId: string;
  merchantPublicId: string;
  productPublicId: string;
}): Promise<PageProps['pageProps']> => {
  const [categories, productDetail] = await Promise.all([
    allCategoriesCache(),
    fetchProductDetail({
      session,
      token,
      requestId,
      merchantPublicId,
      productPublicId,
    }),
  ]);

  const category = merchant
    ? await getCategory(merchant.category_id)
    : undefined;
  return {
    merchantDetail: merchant,
    category,
    categories,
    product: productDetail,
    bookingServices: productDetail.booking_pages || [],
    productStatus: productDetail.status,
  };
};

const MerchantProductDetails: NextPage<Props> = (props) => {
  const {
    isUnpublishedSite,
    query: { publicId, productId },
  } = props;

  const [{ pageProps, isProtected }] = useState<State>({
    pageProps: props.pageProps,
    isProtected: props.isProtected,
  });
  const { merchantDetail, category } = pageProps ?? {};
  const isFetchSucceeded = pageProps && merchantDetail && category;

  const onPasswordSubmit = useCallback(async (success: boolean) => {
    if (success) {
      window.location.reload();
    }
  }, []);

  return (
    <>
      <RSToastContainer />
      <MerchantSite
        isUnPublished={isUnpublishedSite ?? false}
        isMerchantInfoExist={!!merchantDetail}
        isProtected={isProtected}
        protectedElement={
          <>
            <MetaTags
              noIndex={true}
              opgDisabled={true}
              googleAnalyticsFourTrackingId={
                merchantDetail?.available_google_analytics_four_tracking_id
              }
              faviconImageUrl={merchantDetail?.favicon_image?.url}
            />
            <JsonLd merchant={merchantDetail} />
            <PasswordPage
              publicId={publicId}
              productPublicId={productId}
              onSubmit={onPasswordSubmit}
              {...props}
            />
          </>
        }
        logoImage={merchantDetail?.logo_image || null}
        merchantPublicId={merchantDetail?.public_id || null}
        displayName={merchantDetail?.display_name || null}
        isCoubicApp={props.isCoubicApp}
      >
        {isFetchSucceeded ? (
          <>
            <MetaTags
              title={pageProps.product.name}
              description={pageProps.product.description}
              keywords={merchantDetail.seo_setting?.keyword || ''}
              thumbnailUrl={
                pageProps.product.image.thumb.url ||
                merchantDetail.cover_image.url
              }
              currentPath={buildSiteUrl(
                `/products/${pageProps.product.public_id}`,
              )}
              noIndex={
                merchantDetail.seo_setting?.no_index ||
                pageProps.product.status === 'unpublished'
              }
              googleAnalyticsFourTrackingId={
                merchantDetail.available_google_analytics_four_tracking_id
              }
              faviconImageUrl={merchantDetail?.favicon_image?.url}
            />
            <MerchantProductDetailsPage
              merchantDetail={merchantDetail}
              product={pageProps.product}
              productStatus={pageProps.productStatus}
              bookingServices={pageProps.bookingServices}
              productId={productId}
              isCoubicApp={props.isCoubicApp}
            />
          </>
        ) : (
          <ErrorTemplate statusCode={404} />
        )}
      </MerchantSite>
    </>
  );
};

export default MerchantProductDetails;

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx: Context) => {
    const { query, session, token, requestId, isCoubicApp } = ctx;
    const { publicId: merchantPublicId, productId: productPublicId } = query;

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
      serverResponse: ctx.res,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant } = merchantDetailResponse;

    const repository = ProductRepositoryForSSR(session, token, requestId);
    const { isProtected, isUnpublishedSite } = await repository.IsProtected(
      merchantPublicId,
      productPublicId,
    );

    if (isUnpublishedSite) {
      const props: Props = {
        query,
        session,
        token,
        requestId,
        isProtected,
        isUnpublishedSite,
        isCoubicApp,
      };
      return { props };
    }
    const pageProps = isProtected
      ? undefined
      : await fetchPageProps({
          merchant,
          session,
          token,
          requestId,
          merchantPublicId,
          productPublicId,
        });

    if (pageProps?.product.status === 'unpublished') {
      return {
        notFound: true,
      };
    }

    const props: Props = {
      query,
      session,
      token,
      requestId,
      isProtected,
      isUnpublishedSite,
      pageProps,
      isCoubicApp,
    };
    return { props };
  },
);
